import { ChangeDetectionStrategy, Component, Inject, HostListener, InjectionToken } from '@angular/core';
import { Kit } from './kit';
import { KitInventoryStorage } from './kit-inventory-storage';
import { MdlDialogService, MdlDialogReference } from '@angular-mdl/core';
import { routeSnapshotExtensions } from '../../shared/route-extensions';
import { ActivatedRoute } from '@angular/router';
import { Site } from '../../sites';
import { publishReplay, refCount } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { KitDetail } from './kit-detail';
import { dateTimeComparator, dateRenderer } from '../../covance-ag-grid';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import * as moment from 'moment';

export const KIT_DETAIL_TOKEN = new InjectionToken('KitDetail');

export interface KitDetailGridRowItem extends ICellRendererParams {
    data: KitDetail;
}

@Component({
    selector: 'kit-inventory-detail',
    template: require('./kit-inventory-detail.component.html'),
    styles: [require('./kit-inventory-detail.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KitInventoryDetailComponent {
    public kit: Kit;
    public siteId: number;
    public kitDetails$: Observable<KitDetail[]>;
    public detailColumns: ColDef[];

    constructor(
        route: ActivatedRoute,
        private dialog: MdlDialogReference,
        private dialogService: MdlDialogService,
        private kitInventoryStorage: KitInventoryStorage,
        @Inject(KIT_DETAIL_TOKEN) kit: Kit,
    ) {
        this.detailColumns = [
            {
                headerName: 'Accession',
                field: 'accessionNumber',
                colId: 'accessionNumber'
            },
            {
                headerName: 'Expiration Date',
                field: 'expiresOn',
                colId: 'expiresOn',
                sort: 'asc',
                comparator: dateTimeComparator,
                valueGetter: (params: KitDetailGridRowItem) => {
                    if (!params.data) {
                        return null;
                    }
                    if (params.data.expiresOn.isBefore([1000, 1, 1])) {
                        return 'N/A';
                    }
                    return dateRenderer({ value: params.data.expiresOn });
                }
            },
            {
                headerName: 'Days To Expiration',
                field: 'daysToExp',
                colId: 'daysToExp',
                valueGetter: (params: KitDetailGridRowItem) => {
                    if (!params.data) {
                        return null;
                    }
                    if (params.data.expiresOn.isBefore([1000, 1, 1])) {
                        return 'N/A';
                    }
                    return params.data.expiresOn.diff(moment(), 'days');
                },
                cellStyle: (params: KitDetailGridRowItem) => {
                    if (!params.data) {
                        return null;
                    }
                    if (params.data.expiresOn.isBefore([1000, 1, 1])) {
                        return null;
                    }
                    var numberDays = params.data.expiresOn.diff(moment(), 'days');
                    if (numberDays > 0 && numberDays < 30) {
                        return { color: 'orange' };
                    }
                    if (numberDays <= 0) {
                        return { color: 'red' };
                    }
                    return null;
                },
                tooltip: (params: KitDetailGridRowItem) => {
                    if (!params.data) {
                        return null;
                    }
                    if (params.data.expiresOn.isBefore([1000, 1, 1])) {
                        return params.data.accessionNumber + ' has no expiration date.';
                    }
                    var numberDays = params.data.expiresOn.diff(moment(), 'days');
                    if (numberDays > 0 && numberDays < 30) {
                        return params.data.accessionNumber + ' is within 30 days of expiration.';
                    }
                    if (numberDays <= 0) {
                        return params.data.accessionNumber + ' is past expiration.';
                    }
                    return null;
                }
            }
        ];

        this.kit = kit;
        //get site id from route for investigator, pull from kit object for sponsor
        const site = routeSnapshotExtensions.findData(route.snapshot.firstChild.firstChild.firstChild, 'selectedSite') as Site | null;
        if (site && site.siteId) {
            this.siteId = site && site.siteId;
        } else {
            this.siteId = this.kit.siteId;
        }

        this.kitDetails$ = this.kitInventoryStorage.getKitDetails(this.siteId, this.kit.ctSiteId, this.kit.kitTypeId).pipe(publishReplay(1), refCount()) as Observable<KitDetail[]>;
    }

    @HostListener('document:keydown.esc')
    public close() {
        this.dialog.hide(); 
    }
}